/*
  (c) 2025 Fluxonix Inc.
*/

import React, { useState } from "react";
import { Upload, Search, Mail, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import Papa from "papaparse";

export default function BomSearch() {
  const [bomData, setBomData] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [currentStep, setCurrentStep] = useState("upload");
  const [analysisResults, setAnalysisResults] = useState(null);
  const [alternativesData, setAlternativesData] = useState(null);
  const [emailDraft, setEmailDraft] = useState("");

  // ============================================================
  // FLUXONIX BRANDING (inline styles)
  // ============================================================

  const colors = {
    primary: "#5B4BFF",
    primaryDark: "#4338CA",
    secondary: "#8B5CF6",
    gradient: "linear-gradient(135deg, #5B4BFF, #8B5CF6)",
    bgSoft: "#F7F7FC",
    textDark: "#111827",
    textSoft: "#6B7280",
  };

  const fx = {
    page: {
      minHeight: "100vh",
      padding: "50px 20px",
      background: "linear-gradient(135deg, #EEF2FF, #E0E7FF)",
      fontFamily: "Inter, sans-serif",
    },
    card: {
      maxWidth: "950px",
      margin: "0 auto",
      background: "#fff",
      padding: "45px",
      borderRadius: "24px",
      boxShadow: "0 15px 40px rgba(80, 70, 200, 0.15)",
    },
    h1: {
      fontSize: "32px",
      fontWeight: 800,
      color: colors.textDark,
      marginBottom: "6px",
    },
    subtitle: {
      fontSize: "16px",
      color: colors.textSoft,
      marginBottom: "35px",
    },
    steps: {
      display: "flex",
      justifyContent: "space-between",
      marginBottom: "45px",
    },
    step: {
      textAlign: "center",
      width: "25%",
    },
    stepCircle: (active, completed) => ({
      width: "54px",
      height: "54px",
      borderRadius: "50%",
      background: completed
        ? "#4ADE80"
        : active
        ? colors.primary
        : "#CBD5E1",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#fff",
      margin: "0 auto 10px",
      transition: "0.3s",
    }),
    stepLabel: (active, completed) => ({
      fontSize: "13px",
      fontWeight: 600,
      color: active || completed ? colors.textDark : "#9CA3AF",
    }),
    uploadBox: {
      border: `3px dashed ${colors.primary}`,
      padding: "48px",
      textAlign: "center",
      borderRadius: "20px",
      background: colors.bgSoft,
      cursor: "pointer",
    },
    button: {
      width: "100%",
      padding: "15px",
      fontSize: "16px",
      fontWeight: 700,
      borderRadius: "12px",
      border: "none",
      cursor: "pointer",
      color: "#fff",
      background: colors.gradient,
      marginTop: "16px",
      boxShadow: "0 8px 20px rgba(91,75,255,0.35)",
      transition: "0.25s",
    },
    cardBox: {
      background: colors.bgSoft,
      padding: "15px",
      borderRadius: "12px",
      marginBottom: "10px",
      display: "flex",
      gap: "12px",
      alignItems: "start",
      borderLeft: `4px solid ${colors.secondary}`,
    },
    emailBox: {
      background: colors.bgSoft,
      padding: "22px",
      borderRadius: "14px",
      whiteSpace: "pre-wrap",
      fontSize: "15px",
      color: colors.textDark,
    },
    rowButtons: {
      display: "flex",
      gap: "12px",
      marginTop: "20px",
    },
    btnGreen: {
      flex: 1,
      padding: "15px",
      borderRadius: "12px",
      border: "none",
      background: "#22C55E",
      color: "#fff",
      fontWeight: 700,
      cursor: "pointer",
    },
    btnGray: {
      flex: 1,
      padding: "15px",
      borderRadius: "12px",
      border: "none",
      background: "#6B7280",
      color: "#fff",
      fontWeight: 700,
      cursor: "pointer",
    },
  };

  // ============================================================
  // CSV PARSER
  // ============================================================

  const parseBOM = (text) => {
    const result = Papa.parse(text, {
      header: true,
      skipEmptyLines: true,
    });

    return result.data.filter((row) =>
      Object.values(row).some((v) => v && v.trim?.() !== "")
    );
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const text = await file.text();
    const parsed = parseBOM(text);

    if (!parsed.length) {
      alert("Failed to parse BOM file");
      return;
    }

    setBomData(parsed);
    setCurrentStep("analyze");
  };

  // ============================================================
  // BACKEND API CALLS
  // ============================================================

  const API_BASE = "/public/bom";

  const analyzeBOM = async () => {
    setProcessing(true);
    setCurrentStep("analyzing");

    try {
      const response = await fetch(`${API_BASE}/analyze`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bom: bomData }),
      });

      const data = await response.json();
      setAnalysisResults(data);
      setCurrentStep("alternatives");
    } catch (err) {
      alert("Error analyzing BOM");
    }

    setProcessing(false);
  };

  const findAlternatives = async () => {
    setProcessing(true);
    setCurrentStep("searching");

    try {
      const response = await fetch(`${API_BASE}/alternatives`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          deprecatedParts: analysisResults?.deprecatedParts || [],
        }),
      });

      const data = await response.json();
      setAlternativesData(data);
      setCurrentStep("email");
    } catch (err) {
      alert("Error finding alternatives");
    }

    setProcessing(false);
  };

  const generateEmail = async () => {
    setProcessing(true);

    try {
      const response = await fetch(`${API_BASE}/email-draft`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          analysis: analysisResults,
          alternatives: alternativesData,
        }),
      });

      const data = await response.json();
      // Normalize possible backend outputs
      const email =
        data.body ||
        data.email ||
        data.text ||
        data.raw ||
        (typeof data === "string" ? data : JSON.stringify(data.raw || data, null, 2));

      setEmailDraft(email.trim());
      setCurrentStep("review");
    } catch (err) {
      alert("Error generating email");
    }

    setProcessing(false);
  };

  // ============================================================
  // STEP COMPONENT
  // ============================================================

  const Step = ({ icon: Icon, label, active, completed }) => (
    <div style={fx.step}>
      <div style={fx.stepCircle(active, completed)}>
        {completed ? <CheckCircle size={28} /> : <Icon size={28} />}
      </div>
      <div style={fx.stepLabel(active, completed)}>{label}</div>
    </div>
  );

  // ============================================================
  // RENDER UI
  // ============================================================

  return (
    <div style={fx.page}>
      <div style={fx.card}>
        <h1 style={fx.h1}>Fluxonix BOM AI Agent</h1>
        <p style={fx.subtitle}>
          Automated deprecated part detection & alternative supplier finder.
        </p>

        {/* Steps */}
        <div style={fx.steps}>
          <Step icon={Upload} label="Upload" active={currentStep === "upload"} completed={bomData} />
          <Step icon={Search} label="Analyze" active={currentStep === "analyzing"} completed={analysisResults} />
          <Step icon={Search} label="Alternatives" active={currentStep === "searching"} completed={alternativesData} />
          <Step icon={Mail} label="Email" active={currentStep === "review"} completed={emailDraft} />
        </div>

        {/* Upload */}
        {currentStep === "upload" && (
          <label>
            <div style={fx.uploadBox}>
              <Upload size={55} color={colors.primaryDark} />
              <h3 style={{ fontSize: "20px", fontWeight: 700, marginTop: "14px" }}>
                Upload Bill of Materials
              </h3>
              <p style={{ color: colors.textSoft, marginTop: "6px" }}>
                Upload CSV / TSV exported from Altium, KiCad, Fusion 360, etc.
              </p>
              <input type="file" accept=".csv,.tsv" onChange={handleFileUpload} style={{ display: "none" }} />
            </div>
          </label>
        )}

        {/* Analyze */}
        {currentStep === "analyze" && (
          <>
            <p><strong>Total Parts:</strong> {bomData.length}</p>
            <button style={fx.button} onClick={analyzeBOM}>
              Analyze for Deprecated Parts
            </button>
          </>
        )}

        {/* Loading */}
        {(currentStep === "analyzing" || currentStep === "searching") && processing && (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Loader2 size={45} className="animate-spin" />
            <p style={{ color: colors.textSoft, marginTop: "10px" }}>
              Processing...
            </p>
          </div>
        )}
        {/* Deprecated Parts (after Analyze) */}
        {currentStep === "alternatives" && analysisResults && (
          <>
            <h2 style={{ fontSize: "22px", fontWeight: 700, marginBottom: "12px" }}>
              Deprecated Parts Detected
            </h2>

            {analysisResults.deprecatedParts?.length === 0 && (
              <p style={{ color: colors.textSoft, marginBottom: "20px" }}>
                No deprecated parts were identified in this BOM.
              </p>
            )}

            <div style={{ maxHeight: "300px", overflowY: "auto", marginBottom: "20px" }}>
              {analysisResults.deprecatedParts?.map((part, i) => (
                <div key={i} style={fx.cardBox}>
                  <AlertCircle size={20} color="#F87171" />
                  <div>
                    <p style={{ fontSize: "15px", fontWeight: 700 }}>
                      {part.partNumber}
                    </p>
                    <p style={{ fontSize: "14px", color: colors.textSoft }}>
                      {part.description}
                    </p>
                    <p style={{ fontSize: "13px", color: "#EF4444", marginTop: "4px" }}>
                      {part.reason}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Proceed button */}
            <button
              style={fx.button}
              onClick={findAlternatives}
            >
              Find Alternatives & Supplier Options
            </button>
          </>
        )}

        {/* Alternatives */}
        {currentStep === "email" && alternativesData && (
          <>
            <h2 style={{ fontSize: "20px", fontWeight: 700, marginBottom: "10px" }}>
              Alternative Parts Found
            </h2>

            <div style={{ maxHeight: "300px", overflowY: "auto" }}>
              {alternativesData.alternatives?.map((alt, i) => (
                <div key={i} style={fx.cardBox}>
                  <AlertCircle size={20} color="#F87171" />
                  <div>
                    <p style={{ fontWeight: 700 }}>{alt.originalPart}</p>
                    {alt.suggestions.map((s, j) => (
                      <p key={j} style={{ fontSize: "14px", marginTop: "4px" }}>
                        <strong>{s.partNumber}</strong> — {s.supplier} (${s.estimatedPrice})
                      </p>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <button style={fx.button} onClick={generateEmail}>
              Generate Email Request
            </button>
          </>
        )}

        {/* Email Draft */}
        {currentStep === "review" && emailDraft && (
        <>
          <h2 style={{ fontSize: "22px", fontWeight: 700, marginBottom: "12px" }}>
            Email Draft
          </h2>

          <div
            style={{
              background: colors.bgSoft,
              padding: "25px",
              borderRadius: "16px",
              fontSize: "15px",
              lineHeight: "1.6",
              color: colors.textDark,
              whiteSpace: "pre-wrap",
              border: "1px solid #E5E7EB",
              boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
            }}
          >
            {emailDraft}
          </div>

          <div style={fx.rowButtons}>
            <button
              style={fx.btnGreen}
              onClick={() => navigator.clipboard.writeText(emailDraft)}
            >
              Copy Email
            </button>

            <button
              style={fx.btnGray}
              onClick={() => {
                setBomData(null);
                setAnalysisResults(null);
                setAlternativesData(null);
                setEmailDraft("");
                setCurrentStep("upload");
              }}
            >
              Restart
            </button>
          </div>
        </>
      )}
      </div>
    </div>
  );
}